import {
  Field,
  Input,
  Label,
  Radio,
  RadioGroup,
  RadioGroupOnChangeData,
  Text,
} from '@fluentui/react-components';
import { AddRegular } from '@fluentui/react-icons';
import {
  ChatThreadType,
  ChatUserType,
  GuardianChatParticipantCandidatesResponse,
} from '@mnspro-cloud/services/lib/mnspro-cloud/api/v2';
import { uniqBy } from 'lodash';
import React, { FormEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../Button';
import { ResponsiveDialog } from '../../ResponsiveDialog/ResponsiveDialog';
import { useChatContextOptional } from '../ChatContext';
import { ChatParticipantChip } from '../ChatParticipantChip/ChatParticipantChip';
import { ChatParticipantPickerDialog } from '../ChatParticipantPickerDialog/ChatParticipantPickerDialog';
import './ChatCreateDialog.scss';

export type ChatCreateFormValues = {
  topic: string;
  type: ChatThreadType;
  participants: GuardianChatParticipantCandidatesResponse[];
};

interface ChatCreateDialogProps {
  candidates?: GuardianChatParticipantCandidatesResponse[];
  open?: boolean;
  onClose: () => void;
  onSubmit?: (values: ChatCreateFormValues) => void;
}

const isWardCandidate = (candidate: GuardianChatParticipantCandidatesResponse) =>
  candidate.userType === ChatUserType.parent || candidate.userType === ChatUserType.ward;

const initialFormValues: ChatCreateFormValues = {
  topic: '',
  type: ChatThreadType.group,
  participants: [],
};

export const ChatCreateDialog: React.FC<ChatCreateDialogProps> = (props) => {
  const context = useChatContextOptional();
  const open = context?.createChatDialogVisible ?? props.open ?? false;
  const onSubmit = context?.createChatThread ?? props.onSubmit ?? function () {};
  const onClose = props.onClose;
  const currentUser = context?.currentUser;

  const getInitialFormValues = (): ChatCreateFormValues => ({
    ...initialFormValues,
    participants: currentUser ? [currentUser] : [],
  });

  const { t } = useTranslation();
  const [formValues, setFormValues] = useState<ChatCreateFormValues>(getInitialFormValues);
  const [showParticipantPicker, setShowParticipantPicker] = useState(false);
  const [topicAutoGenerated, setTopicAutoGenerated] = useState(false);

  const handleClose = () => {
    setFormValues(getInitialFormValues());
    setShowParticipantPicker(false);
    setTopicAutoGenerated(false);
    onClose();
  };

  const handleSubmit = () => {
    onSubmit(formValues);
    setFormValues(getInitialFormValues());
    setTopicAutoGenerated(false);
  };

  /**
   * Type of the chat is changed (group/direct)
   */
  const handleChatTypeChange = (
    _event: FormEvent<HTMLDivElement>,
    data: RadioGroupOnChangeData,
  ) => {
    const newType = data.value as ChatThreadType;
    setFormValues((prev) => ({
      ...prev,
      type: newType,
      topic: topicAutoGenerated ? '' : prev.topic,
      participants:
        // When direct chat remove all participants, except self, to avoid adding
        // more than one child and teacher.
        newType == ChatThreadType.group ? prev.participants : currentUser ? [currentUser] : [],
    }));
    setTopicAutoGenerated(false);
  };

  const handleParticipantsSelected = (selected: GuardianChatParticipantCandidatesResponse[]) => {
    setShowParticipantPicker(false);
    const ward = selected.find(isWardCandidate);
    const mayReplaceTopic = formValues.topic.trim() === '' || topicAutoGenerated;
    const applyAutoTopic =
      ward !== undefined && formValues.type === ChatThreadType.direct && mayReplaceTopic;

    setFormValues((prev) => {
      const isDirectWardSelection = prev.type === ChatThreadType.direct && ward !== undefined;
      const kept = isDirectWardSelection
        ? prev.participants.filter((p) => !isWardCandidate(p))
        : prev.participants;

      return {
        ...prev,
        participants: uniqBy([...kept, ...selected], 'userId'),
        topic:
          ward && applyAutoTopic
            ? t('pc:chat:createDialog:direct_chat_topic', {
                defaultValue: 'Chat über {{name}}',
                name: ward.displayName,
              })
            : prev.topic,
      };
    });

    if (applyAutoTopic) {
      setTopicAutoGenerated(true);
    }
  };

  const isFormValid = useMemo(() => {
    if (formValues.topic.trim() === '') {
      return false;
    }

    if (formValues.type === ChatThreadType.group) {
      return formValues.participants.length > 0;
    }

    // Direct chat, only one teacher and ward
    const teachers = formValues.participants.filter((p) => p.userType === ChatUserType.teacher);
    const parents = formValues.participants.filter(isWardCandidate);

    return teachers.length === 1 && parents.length === 1;
  }, [formValues]);

  return (
    <>
      {showParticipantPicker && (
        <ChatParticipantPickerDialog
          candidates={props.candidates}
          participantsLoadingState={props.candidates ? 'loaded' : undefined}
          onConfirm={handleParticipantsSelected}
          onCancel={() => setShowParticipantPicker(false)}
          selectionMode={formValues.type === ChatThreadType.direct ? 'single' : 'multi'}
          open
          showTeachers={formValues.type === ChatThreadType.group}
        />
      )}
      <ResponsiveDialog
        open={open}
        header={t('pc:chat:createDialog:header', { defaultValue: 'Neuen Chat erstellen' })}
        className="ChatCreateDialog"
        primaryAction={
          <Button appearance="primary" onClick={handleSubmit} disabled={!isFormValid}>
            {t('pc:chat:createDialog:create_button', { defaultValue: 'Erstellen' })}
          </Button>
        }
        secondaryAction={
          <Button onClick={handleClose}>
            {t('pc:chat:createDialog:cancel_button', { defaultValue: 'Abbrechen' })}
          </Button>
        }
      >
        <div className="flex flex-col gap-2 flex-start">
          <div className="flex flex-col gap-1">
            <Label id="chatNameInputLabel">
              {t('pc:chat:createDialog:chat_name_label', { defaultValue: 'Name des Chats' })}
            </Label>
            <Input
              appearance="filled-darker"
              aria-labelledby="chatNameInputLabel"
              value={formValues.topic}
              onChange={(_event, data) => {
                setTopicAutoGenerated(false);
                setFormValues((prev) => ({ ...prev, topic: data.value }));
              }}
            />
          </div>
          <div className="flex flex-col gap-1">
            <Field
              label={t('pc:chat:createDialog:chat_type_label', { defaultValue: 'Art des Chats' })}
            >
              <RadioGroup
                value={formValues.type === ChatThreadType.direct ? 'Direct' : 'Group'}
                defaultValue={formValues.type === ChatThreadType.direct ? 'Direct' : 'Group'}
                onChange={handleChatTypeChange}
              >
                <Radio
                  value="Group"
                  label={t('pc:chat:createDialog:type_group', { defaultValue: 'Gruppenchat' })}
                />
                <Radio
                  value="Direct"
                  label={t('pc:chat:createDialog:type_direct', { defaultValue: '1-zu-1 Chat' })}
                />
              </RadioGroup>
            </Field>
          </div>
        </div>
        <div className="participantsWrapper flex flex-col gap-2">
          <Label>
            {t('pc:chat:createDialog:participants_label', { defaultValue: 'Teilnehmer' })}
          </Label>
          {formValues.participants.length === 0 && (
            <div className="noParticipantSelectedMsg">
              <Text size={200}>
                {t('pc:chat:createDialog:no_participants_selected', {
                  defaultValue: 'Noch keine Teilnehmer ausgewählt',
                })}
              </Text>
            </div>
          )}
          {formValues.participants.length > 0 && (
            <div className="selectedChips">
              {formValues.participants.map((candidate) => {
                const isCurrentUser = currentUser?.userId === candidate.userId;
                return (
                  <ChatParticipantChip
                    key={candidate.userId}
                    displayName={candidate.displayName}
                    onRemoveClick={
                      isCurrentUser
                        ? undefined
                        : () =>
                            setFormValues((prev) => ({
                              ...prev,
                              participants: prev.participants.filter(
                                (p) => p.userId !== candidate.userId,
                              ),
                            }))
                    }
                  />
                );
              })}
            </div>
          )}
          <Button
            className="addParticipantBtn"
            onClick={() => setShowParticipantPicker(true)}
            appearance="transparent"
            icon={<AddRegular />}
          >
            {t('pc:chat:createDialog:add_participants', { defaultValue: 'Teilnehmer hinzufügen' })}
          </Button>
        </div>
      </ResponsiveDialog>
    </>
  );
};
